import React, { useState, useEffect } from "react";
import { Lock, RotateCcw, Loader2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";

const Reset = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ password: "", confirmpassword: "" });

  // Token validation on mount
  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await fetch("https://orchestration.trackdsa.in/user/reset", {
          method: "POST",
          body: JSON.stringify({ token }),
          headers: { "Content-type": "application/json; charset=UTF-8" },
        });
        const data = await response.json();
        if (!data.status) throw new Error("Token expired");
      } catch (err) {
        toast.error("Token expired or invalid!");
        navigate("/forgetpassword", { replace: true });
      }
    };
    validateToken();
  }, [navigate, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmpassword) {
      toast.error("Passwords do not match!");
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await fetch("https://orchestration.trackdsa.in/user/reset", {
        method: "POST",
        body: JSON.stringify({ token, password: formData.password }),
        headers: { "Content-type": "application/json; charset=UTF-8" },
      });
      const data = await response.json();

      if (data.status) {
        toast.success("Password reset successful!");
        navigate("/login", { replace: true });
      } else {
        toast.error("Token expired!");
        navigate("/forgetpassword", { replace: true });
      }
    } catch (err) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-xl p-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-slate-800">Set New Password</h2>
          <p className="text-slate-500 mt-2">Enter your new secure password below.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">New Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                placeholder="••••••••"
              />
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Confirm Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="password"
                required
                value={formData.confirmpassword}
                onChange={(e) => setFormData({ ...formData, confirmpassword: e.target.value })}
                className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                placeholder="••••••••"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-all shadow-sm"
          >
            {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Reset Password"}
            {!isSubmitting && <RotateCcw className="w-4 h-4" />}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Reset;